import { getBondChatSocket } from "./bondchat/generalChatSingleton";
import { requireNetwork } from "../../utils/requireNetwork";

const OFFLINE_MSG = "You're offline. Connect to the internet to continue.";

// Money-moving P2P events must carry the user's JWT — the server no longer
// trusts client-supplied userId.
const withAuthToken = (payload = {}) => {
  let token = null;
  try { token = localStorage.getItem("betbond_app_token"); } catch {}
  return token ? { ...payload, token } : payload;
};

const waitForSocket = (timeoutMs = 15000) =>
  new Promise((resolve, reject) => {
    const socket = getBondChatSocket();
    if (socket?.connected) {
      resolve(socket);
      return;
    }
    if (!socket) {
      reject(new Error("Connection not ready. Please wait a moment and try again."));
      return;
    }
    const timer = setTimeout(() => {
      reject(new Error("Connection timed out. Check your network and try again."));
    }, timeoutMs);
    const onConnect = () => {
      clearTimeout(timer);
      socket.off("connect", onConnect);
      resolve(socket);
    };
    socket.on("connect", onConnect);
    try {
      socket.connect();
    } catch {
      clearTimeout(timer);
      socket.off("connect", onConnect);
      reject(new Error("Could not connect to the server."));
    }
  });

const waitForP2PSocketResult = async ({
  payload,
  successEvent,
  errorContext,
  emit,
  matchSuccess = (response, targetId) => {
    const id =
      response?.p2pBet?._id != null
        ? String(response.p2pBet._id)
        : response?.p2pId != null
          ? String(response.p2pId)
          : "";
    return id === targetId;
  },
  timeoutMs = 45000,
}) => {
  if (!requireNetwork(OFFLINE_MSG)) {
    throw new Error(OFFLINE_MSG);
  }
  const socket = await waitForSocket();
  const targetId = String(payload?.p2pId ?? "");

  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      socket.off(successEvent, onSuccess);
      socket.off("p2pError", onError);
    };

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error("Request timed out. Check your connection and try again."));
    }, timeoutMs);

    const onSuccess = (response) => {
      if (!matchSuccess(response, targetId)) return;
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(response);
    };

    const onError = (errPayload) => {
      if (errorContext) {
        const ctx = errPayload?.context;
        const eid = errPayload?.p2pId != null ? String(errPayload.p2pId) : "";
        if (ctx !== errorContext || (targetId && eid && eid !== targetId)) return;
      }
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      const msg =
        (errPayload?.error && String(errPayload.error).trim()) ||
        (errPayload?.message && String(errPayload.message).trim()) ||
        "P2P request failed.";
      const error = new Error(msg);
      error.silent = true;
      reject(error);
    };

    socket.on(successEvent, onSuccess);
    socket.on("p2pError", onError);
    emit(socket, withAuthToken(payload));
  });
};

export const joinP2PBetWithAck = (payload, timeoutMs) =>
  waitForP2PSocketResult({
    payload,
    successEvent: "p2pBetJoinedSuccess",
    errorContext: "joinP2PBet",
    emit: (socket, p) => socket.emit("joinP2PBet", p),
    timeoutMs,
  });

export const acceptP2PInviteWithAck = (payload, timeoutMs) =>
  waitForP2PSocketResult({
    payload,
    successEvent: "p2pInviteAcceptedSuccess",
    errorContext: "acceptP2PInvite",
    emit: (socket, p) => socket.emit("acceptP2PInvite", p),
    timeoutMs,
  });

export const rejectP2PInviteWithAck = (payload, timeoutMs) =>
  waitForP2PSocketResult({
    payload,
    successEvent: "p2pInviteRejectedSuccess",
    errorContext: "rejectP2PInvite",
    matchSuccess: (response, targetId) => {
      const id =
        response?.p2pBet?._id != null
          ? String(response.p2pBet._id)
          : response?.p2pId != null
            ? String(response.p2pId)
            : "";
      return id === targetId;
    },
    emit: (socket, p) => socket.emit("rejectP2PInvite", p),
    timeoutMs,
  });

export const cancelP2PBetWithAck = (payload, timeoutMs) =>
  waitForP2PSocketResult({
    payload,
    successEvent: "p2pBetCancelledSuccess",
    errorContext: null,
    matchSuccess: (response, targetId) =>
      String(response?.p2pId ?? "") === targetId,
    emit: (socket, p) => socket.emit("cancelP2PBet", p),
    timeoutMs,
  });

export const editP2PBetWithAck = (payload, timeoutMs) =>
  waitForP2PSocketResult({
    payload,
    successEvent: "p2pBetEditedSuccess",
    errorContext: "editP2PBet",
    emit: (socket, p) => socket.emit("editP2PBet", p),
    timeoutMs,
  });

export const createP2PBetWithAck = (payload, timeoutMs) =>
  waitForP2PSocketResult({
    payload,
    successEvent: "p2pBetCreated",
    errorContext: null,
    matchSuccess: (response) => Boolean(response?.p2pBet?._id),
    emit: (socket, p) => socket.emit("createP2PBet", p),
    timeoutMs,
  });
